import { cn } from "@/lib/utils";
import { Crosshair, ChatCircle, Sparkle, FilmReel, FileText, Student } from "@phosphor-icons/react";
import {
  useActivityFeed,
  type ActivityKind,
} from "@/features/dashboard/hooks/useActivityFeed";

const ICON_BY_KIND: Record<ActivityKind, typeof Crosshair> = {
  consulta: ChatCircle,
  decodificar: Crosshair,
  criativo: FilmReel,
  roteiro: FileText,
  simulador: Student,
};

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "agora";
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `há ${min} min`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `há ${hr} h`;
  const days = Math.floor(hr / 24);
  if (days === 1) return "ontem";
  if (days < 7) return `há ${days} dias`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `há ${weeks} sem`;
  const months = Math.floor(days / 30);
  return `há ${months} mês${months === 1 ? "" : "es"}`;
}

export function RecentActivity() {
  const { items, isLoading } = useActivityFeed(20);
  const visible = items.slice(0, 5);

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <h3 className="text-lg font-semibold text-foreground mb-4">Atividade Recente</h3>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Sparkle weight="duotone" className="w-6 h-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Sem atividade ainda. Use os módulos para começar.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {visible.map((item, index) => {
            const Icon = ICON_BY_KIND[item.kind];
            return (
              <div
                key={item.id}
                className={cn(
                  "flex items-start gap-3 pb-4",
                  index !== visible.length - 1 && "border-b border-border/30",
                )}
              >
                <div className="p-2 rounded-lg bg-primary/10 flex-shrink-0">
                  <Icon weight="duotone" className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{item.title}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.description}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                  {relativeTime(item.createdAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
